/**
 * Shared saving overlay for simulation modules.
 *
 * Mirrors the overlay Reading uses today (`bg-background/80`, backdrop blur,
 * `Loader2` spinner, card). Rendered when the module is persisting answers
 * so the user cannot interact with the interface mid-save.
 *
 * Owns no state; the parent controls the `open` flag.
 */

import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';

export interface SimulationSavingOverlayProps {
  /** Whether the overlay is visible. */
  readonly open: boolean;
  /** Overlay message; defaults to `simulation.active.savingAnswers`. */
  readonly message?: string;
}

export function SimulationSavingOverlay({ open, message }: SimulationSavingOverlayProps) {
  const { t } = useTranslation();
  if (!open) return null;
  const text = message
    ?? t('simulation.active.savingAnswers', { defaultValue: 'Saving your answers...' });

  return (
    <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
      <Card className="p-8 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
        <p className="font-ui text-muted-foreground">{text}</p>
      </Card>
    </div>
  );
}
